"use client";

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Loader,
} from "@devhop/ui";
import { Link, useParams } from "@tanstack/react-router";
import { MapPin, PackageSearch, Truck } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { shopBreadcrumbs } from "@/modules/merchant/presentation/lib/shop-breadcrumb";
import { hasShopFeature } from "@/modules/merchant/presentation/lib/has-shop-feature";
import {
  useEmptyWarehouseLocationsReportQuery,
  useReceiveByLocationReportQuery,
  useShopBranchesListQuery,
  useUnlocatedInventoryReportQuery,
} from "@/modules/merchant/presentation/api/queries";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { RequireMerchantPermission } from "@/modules/merchant/presentation/ui/RequireMerchantPermission";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";

type ReportTab = "unlocated" | "empty" | "receive";

const SKU_DETAIL_ROUTE =
  "/app/shops/$shopId/inventory/skus/$shopProductSkuId" as const;
const PO_DETAIL_ROUTE = "/app/shops/$shopId/purchase-orders/$purchaseOrderId" as const;
const LAYOUT_ROUTE = "/app/shops/$shopId/inventory/locations" as const;

const PAGE_SIZE = 20;

function ReportPagination({
  total,
  offset,
  limit,
  onPageChange,
}: {
  total: number;
  offset: number;
  limit: number;
  onPageChange: (nextOffset: number) => void;
}) {
  const pageEnd = Math.min(offset + limit, total);
  const hasPrev = offset > 0;
  const hasNext = offset + limit < total;

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-border border-t px-4 py-3 text-sm">
      <span className="text-muted-foreground">
        {total === 0 ? "0" : `${offset + 1}–${pageEnd}`} ຈາກ {total}
      </span>
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!hasPrev}
          onClick={() => onPageChange(Math.max(0, offset - limit))}
        >
          ກ່ອນໜ້າ
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!hasNext}
          onClick={() => onPageChange(offset + limit)}
        >
          ຖັດໄປ
        </Button>
      </div>
    </div>
  );
}

export function ShopReportsWarehouseLocationsPage() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;

  const [tab, setTab] = useState<ReportTab>("unlocated");
  const [branchId, setBranchId] = useState("");
  const [searchQ, setSearchQ] = useState("");
  const [receivedFrom, setReceivedFrom] = useState("");
  const [receivedTo, setReceivedTo] = useState("");
  const [offset, setOffset] = useState(0);

  const { currentShop } = useMerchantShops(shopId);
  const warehouseLayoutOn = hasShopFeature(
    currentShop?.featureFlags,
    "warehouse_layout",
  );

  const branchesQuery = useShopBranchesListQuery(shopId);
  const branches = branchesQuery.data ?? [];
  const numberFmt = useMemo(() => new Intl.NumberFormat("lo-LA"), []);

  useEffect(() => {
    if (tab === "receive") return;
    if (!branchId && branches.length > 0) {
      const firstActive = branches.find(
        (b) => (b as { isActive?: boolean }).isActive !== false,
      );
      setBranchId(firstActive?.id ?? branches[0]!.id);
    }
  }, [tab, branchId, branches]);

  useEffect(() => {
    setOffset(0);
  }, [tab, branchId, searchQ, receivedFrom, receivedTo]);

  const unlocatedQuery = useUnlocatedInventoryReportQuery(
    shopId,
    tab === "unlocated" && branchId
      ? { branchId, q: searchQ.trim() || undefined, limit: PAGE_SIZE, offset }
      : undefined,
  );

  const emptyQuery = useEmptyWarehouseLocationsReportQuery(
    shopId,
    tab === "empty" && branchId
      ? { branchId, limit: PAGE_SIZE, offset }
      : undefined,
  );

  const receiveQuery = useReceiveByLocationReportQuery(
    shopId,
    tab === "receive"
      ? {
          branchId: branchId || undefined,
          receivedFrom: receivedFrom || undefined,
          receivedTo: receivedTo || undefined,
          limit: PAGE_SIZE,
          offset,
        }
      : undefined,
  );

  const activeQuery =
    tab === "unlocated"
      ? unlocatedQuery
      : tab === "empty"
        ? emptyQuery
        : receiveQuery;

  const tabs: { id: ReportTab; label: string; icon: typeof MapPin }[] = [
    { id: "unlocated", label: "ຍັງບໍ່ຈັດຊ່ອງ", icon: PackageSearch },
    { id: "empty", label: "ຊ່ອງວ່າງ", icon: MapPin },
    { id: "receive", label: "ຮັບເຂົ້າຕາມຊ່ອງ", icon: Truck },
  ];

  return (
    <RequireMerchantPermission permission={MerchantPermissions.inventory.read}>
      <Header>
        <Breadcrumb items={shopBreadcrumbs.reportsWarehouseLocations(shopId)} />
      </Header>
      <Main>
        <div className="mb-6">
          <h1 className="font-heading font-semibold text-foreground text-xl">
            ລາຍງານຕຳແໜ່ງສາງ
          </h1>
          <p className="mt-1 text-muted-foreground text-sm">
            ສິນຄ້າທີ່ຍັງບໍ່ຈັດຊ່ອງ, ຊ່ອງວ່າງ, ແລະ ການຮັບເຂົ້າຕາມຊ່ອງ (GRN)
          </p>
        </div>

        <div className="mb-4 flex flex-wrap gap-2">
          {tabs.map((t) => (
            <Button
              key={t.id}
              type="button"
              variant={tab === t.id ? "default" : "outline"}
              size="sm"
              className="gap-1.5"
              onClick={() => setTab(t.id)}
            >
              <t.icon className="size-4" aria-hidden />
              {t.label}
            </Button>
          ))}
        </div>

        <Card className="mb-4">
          <CardContent className="grid gap-4 pt-6 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-1.5">
              <Label htmlFor="report-branch">ສາຂາ</Label>
              <select
                id="report-branch"
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={branchId}
                onChange={(e) => setBranchId(e.target.value)}
                disabled={branchesQuery.isLoading}
              >
                {tab === "receive" ? (
                  <option value="">ທຸກສາຂາ</option>
                ) : null}
                {branches.map((b) => {
                  const inactive =
                    (b as { isActive?: boolean }).isActive === false;
                  return (
                  <option
                    key={b.id}
                    value={b.id}
                    disabled={inactive}
                  >
                    {b.name}
                    {inactive ? " (ປິດ)" : ""}
                  </option>
                  );
                })}
              </select>
              {tab === "receive" ? (
                <p className="text-muted-foreground text-xs">
                  ເລືອກ «ທຸກສາຂາ» ເພື່ອເບິ່ງທັງຮ້ານ
                </p>
              ) : null}
            </div>

            {tab === "unlocated" ? (
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="report-search">ຄົ້ນຫາ SKU / ຊື່ສິນຄ້າ</Label>
                <Input
                  id="report-search"
                  value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                  placeholder="ພິມຊື່, SKU, barcode…"
                />
              </div>
            ) : null}

            {tab === "receive" ? (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="report-from">ຮັບຕັ້ງແຕ່</Label>
                  <Input
                    id="report-from"
                    type="date"
                    value={receivedFrom}
                    onChange={(e) => setReceivedFrom(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="report-to">ຮັບຈົນຮອດ</Label>
                  <Input
                    id="report-to"
                    type="date"
                    value={receivedTo}
                    onChange={(e) => setReceivedTo(e.target.value)}
                  />
                </div>
              </>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {tabs.find((t) => t.id === tab)?.label}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {activeQuery.isLoading ? (
              <div className="flex justify-center py-12">
                <Loader />
              </div>
            ) : activeQuery.isError ? (
              <p className="px-4 py-8 text-destructive text-sm">
                ໂຫຼດລາຍງານບໍ່ສຳເລັດ
              </p>
            ) : tab === "unlocated" ? (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[640px] text-sm">
                    <thead>
                      <tr className="border-border border-b bg-muted/40 text-left text-muted-foreground">
                        <th className="px-4 py-2 font-medium">ສິນຄ້າ</th>
                        <th className="px-4 py-2 font-medium">SKU</th>
                        <th className="px-4 py-2 text-right font-medium">on hand</th>
                        <th className="px-4 py-2 text-right font-medium">ຈັດແລ້ວ</th>
                        <th className="px-4 py-2 text-right font-medium">ຍັງບໍ່ຈັດ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(unlocatedQuery.data?.data ?? []).length === 0 ? (
                        <tr>
                          <td
                            colSpan={5}
                            className="px-4 py-8 text-center text-muted-foreground"
                          >
                            ບໍ່ມີສິນຄ້າທີ່ຍັງບໍ່ຈັດຊ່ອງ
                          </td>
                        </tr>
                      ) : (
                        (unlocatedQuery.data?.data ?? []).map((row) => (
                          <tr
                            key={row.shopProductSkuId}
                            className="border-border border-b last:border-0"
                          >
                            <td className="px-4 py-2">
                              {shopId ? (
                                <Link
                                  to={SKU_DETAIL_ROUTE}
                                  params={{
                                    shopId,
                                    shopProductSkuId: row.shopProductSkuId,
                                  }}
                                  search={{ branchId }}
                                  className="font-medium text-primary hover:underline"
                                >
                                  {row.productName}
                                </Link>
                              ) : (
                                row.productName
                              )}
                            </td>
                            <td className="px-4 py-2 font-mono text-xs">
                              {row.shopSku}
                            </td>
                            <td className="px-4 py-2 text-right tabular-nums">
                              {numberFmt.format(row.onHand)}
                            </td>
                            <td className="px-4 py-2 text-right tabular-nums">
                              {numberFmt.format(row.locatedQty)}
                            </td>
                            <td className="px-4 py-2 text-right font-medium tabular-nums text-amber-700 dark:text-amber-400">
                              {numberFmt.format(row.unlocatedQty)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                <ReportPagination
                  total={unlocatedQuery.data?.meta.total ?? 0}
                  offset={offset}
                  limit={PAGE_SIZE}
                  onPageChange={setOffset}
                />
              </>
            ) : tab === "empty" ? (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[520px] text-sm">
                    <thead>
                      <tr className="border-border border-b bg-muted/40 text-left text-muted-foreground">
                        <th className="px-4 py-2 font-medium">ລະຫັດຊ່ອງ</th>
                        <th className="px-4 py-2 font-medium">ໂຊນ</th>
                        <th className="px-4 py-2 font-medium">ຊື່</th>
                        <th className="px-4 py-2 font-medium">ສະຖານະ</th>
                        {warehouseLayoutOn ? (
                          <th className="px-4 py-2 font-medium">ແຜນຜັງ</th>
                        ) : null}
                      </tr>
                    </thead>
                    <tbody>
                      {(emptyQuery.data?.data ?? []).length === 0 ? (
                        <tr>
                          <td
                            colSpan={warehouseLayoutOn ? 5 : 4}
                            className="px-4 py-8 text-center text-muted-foreground"
                          >
                            ບໍ່ມີຊ່ອງວ່າງ (ທຸກຊ່ອງມີສິນຄ້າ)
                          </td>
                        </tr>
                      ) : (
                        (emptyQuery.data?.data ?? []).map((row) => (
                          <tr
                            key={row.locationId}
                            className="border-border border-b last:border-0"
                          >
                            <td className="px-4 py-2 font-mono text-xs">
                              {row.locationCode}
                            </td>
                            <td className="px-4 py-2">
                              {row.zoneCode}
                              <span className="text-muted-foreground">
                                {" "}
                                · {row.zoneName}
                              </span>
                            </td>
                            <td className="px-4 py-2">
                              {row.locationName ?? "—"}
                            </td>
                            <td className="px-4 py-2">
                              {row.isDefaultReceive ? (
                                <Badge variant="secondary">ຮັບເຂົ້າເລີ່ມຕົ້ນ</Badge>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </td>
                            {warehouseLayoutOn && shopId && branchId ? (
                              <td className="px-4 py-2">
                                <Link
                                  to={LAYOUT_ROUTE}
                                  params={{ shopId }}
                                  search={{
                                    tab: "layout",
                                    branchId,
                                    focusLocationId: row.locationId,
                                  }}
                                  className="text-primary text-xs underline-offset-2 hover:underline"
                                >
                                  ເບິ່ງໃນແຜນຜັງ
                                </Link>
                              </td>
                            ) : null}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                <ReportPagination
                  total={emptyQuery.data?.meta.total ?? 0}
                  offset={offset}
                  limit={PAGE_SIZE}
                  onPageChange={setOffset}
                />
              </>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[760px] text-sm">
                    <thead>
                      <tr className="border-border border-b bg-muted/40 text-left text-muted-foreground">
                        <th className="px-4 py-2 font-medium">ວັນທີຮັບ</th>
                        <th className="px-4 py-2 font-medium">ສາຂາ</th>
                        <th className="px-4 py-2 font-medium">ຊ່ອງ</th>
                        <th className="px-4 py-2 font-medium">ສິນຄ້າ</th>
                        <th className="px-4 py-2 font-medium">PO</th>
                        <th className="px-4 py-2 text-right font-medium">qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(receiveQuery.data?.data ?? []).length === 0 ? (
                        <tr>
                          <td
                            colSpan={6}
                            className="px-4 py-8 text-center text-muted-foreground"
                          >
                            ບໍ່ມີຂໍ້ມູນຮັບເຂົ້າ
                          </td>
                        </tr>
                      ) : (
                        (receiveQuery.data?.data ?? []).map((row) => (
                          <tr
                            key={row.lineId}
                            className="border-border border-b last:border-0"
                          >
                            <td className="px-4 py-2 whitespace-nowrap text-muted-foreground">
                              {formatDateTimeLocal(row.receivedAt)}
                            </td>
                            <td className="px-4 py-2">{row.branchName}</td>
                            <td className="px-4 py-2 font-mono text-xs">
                              {row.locationCode ?? (
                                <span className="text-muted-foreground">
                                  ບໍ່ລະບຸ
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-2">
                              <span className="block">{row.productName}</span>
                              <span className="font-mono text-muted-foreground text-xs">
                                {row.shopSku}
                              </span>
                            </td>
                            <td className="px-4 py-2">
                              {row.purchaseOrderId && shopId ? (
                                <Link
                                  to={PO_DETAIL_ROUTE}
                                  params={{
                                    shopId,
                                    purchaseOrderId: row.purchaseOrderId,
                                  }}
                                  className="text-primary hover:underline"
                                >
                                  {row.poNumber ?? "PO"}
                                </Link>
                              ) : (
                                row.poNumber ?? "—"
                              )}
                            </td>
                            <td className="px-4 py-2 text-right tabular-nums">
                              {numberFmt.format(row.qtyReceived)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                <ReportPagination
                  total={receiveQuery.data?.meta.total ?? 0}
                  offset={offset}
                  limit={PAGE_SIZE}
                  onPageChange={setOffset}
                />
              </>
            )}
          </CardContent>
        </Card>
      </Main>
    </RequireMerchantPermission>
  );
}
